import { Box, Typography } from '@mui/material'
import PropTypes from 'prop-types'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
// import { useMutation } from 'react-query'
import { useCountdown } from '../../hooks/useCountdown'
import { useGetUser } from '../../hooks/useGetUser'
// import { BaseService } from '../../services/BaseService'
import QuestionHeader from './QuestionHeader'
import { yupResolver } from '@hookform/resolvers/yup'
import { createQuestionValidation } from '../../validations/questionValidations'
import { useQeustion, useQuestionDispatch } from '../../context/QuestionContext/QuestionContext'
import {
  NEXT_QUESTION,
  SET_CURRENT_QUESTION,
  RESET_SUBTEST,
  SET_GLOBAL_COUNT,
} from '../../reducers/reducesTypes'
import { useModule } from '../../context/ModuleContext/ModuleContext'

// async function postAnswer(payload) {
//   const service = new BaseService('/m_activity_2')
//   const res = await service.postAnswer(payload)
//   return res.data
// }

const WizardKraep = React.memo(({ subtype, questions, onFinish, children, freeze }) => {
  // console.log({ subtype, questions })
  const { modules } = useModule()
  const dataQuestion = useQeustion()
  const { user } = useGetUser()
  const dispatch = useQuestionDispatch()

  // read stored progress for this subtest if any (initial read)
  const questionStorage = useMemo(() => {
    const raw = localStorage.getItem('questionKraep')
    if (!raw) return null
    try {
      return JSON.parse(raw)
    } catch {
      return null
    }
  }, [])

  const savedForThis = questionStorage?.data?.find((d) => d.subType === subtype.type_code)

  function parseWaktu(waktu) {
    if (!waktu) return { minutes: 0, seconds: 0 }

    const num = parseFloat(waktu)

    // Kalau integer (3, 5, dst) → menit
    if (Number.isInteger(num)) {
      return { minutes: num, seconds: 0 }
    }

    // Kalau desimal (0.5, 1.5, dst) → ubah ke detik
    const totalSeconds = num * 60
    const minutes = Math.floor(totalSeconds / 60)
    const seconds = Math.round(totalSeconds % 60)

    return { minutes, seconds }
  }

  const { minutes: initMin, seconds: initSec } = parseWaktu(subtype?.waktu)

  // countdown
  const [minutes, seconds, isStop] = useCountdown({
    initialMinute: savedForThis?.data?.minutes ?? initMin,
    initialSeconds: savedForThis?.data?.seconds ?? initSec,
    freeze: subtype?.freeze_time ?? freeze ?? false,
  })

  // --- STATE UI & LOGIC FIXED ---
  const initialIndex = savedForThis
    ? Math.min(Math.max(0, (savedForThis.data.currentPage || 1) - 1), questions.length - 1)
    : 0
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(initialIndex)

  // console.log({ i: initialIndex, c: currentQuestionIndex, s: savedForThis?.data?.currentPage })

  function normalizeAnswers(values, subtype, questions) {
    if (!['WU', 'FA'].includes(subtype?.type_code)) return values

    const letterMap = ['a', 'b', 'c', 'd', 'e']
    const newValues = {}

    Object.entries(values).forEach(([key, val]) => {
      if (!val) {
        newValues[key] = val
        return
      }

      const qIndex = parseInt(key.replace('question_', ''), 10) - 1
      const q = questions[qIndex]
      const options = [q?.pilihan_1, q?.pilihan_2, q?.pilihan_3, q?.pilihan_4, q?.pilihan_5]

      const optIndex = options.findIndex((opt) => opt === val)
      newValues[key] = optIndex >= 0 ? letterMap[optIndex] : val
    })

    return newValues
  }

  // const [globalIndex, setGlobalIndex] = useState(questionStorage?.globalCount ?? 0)

  // eslint-disable-next-line
  const [globalIndex, setGlobalIndex] = useState(() => {
    try {
      const raw = localStorage.getItem('questionKraep')
      const parsed = raw ? JSON.parse(raw) : null
      return parsed?.globalCount ?? 0
    } catch {
      return 0
    }
  })

  useEffect(() => {
    try {
      const parsed = JSON.parse(localStorage.getItem('questionKraep') || '{}')
      if (parsed?.globalCount != null) {
        setGlobalIndex(parsed.globalCount)
        dispatch({ type: SET_GLOBAL_COUNT, payload: parsed.globalCount })
      }
    } catch {
      setGlobalIndex(0)
      dispatch({ type: SET_GLOBAL_COUNT, payload: 0 })
    }
    // eslint-disable-next-line
  }, [])

  const [subtestFinished, setSubtestFinished] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)
  const hasFinalSubmittedRef = useRef(false)

  const totalQuestions = questions.length
  const maxCount = totalQuestions - 1

  // validation resolver
  const resolver = useMemo(() => {
    return yupResolver(
      createQuestionValidation(
        dataQuestion.questionsList[currentQuestionIndex]?.type_sub_modul,
        currentQuestionIndex + 1,
        { timer: { minutes, seconds } },
        {
          maxNumber: dataQuestion.questionsList[currentQuestionIndex]?.esai_length,
          question: dataQuestion.questionsList[currentQuestionIndex],
        },
      ),
    )
    // eslint-disable-next-line
  }, [currentQuestionIndex, minutes, seconds, questions, dataQuestion.moduleType])

  const form = useForm({
    defaultValues: savedForThis?.data?.questions || {},
    resolver,
  })

  // const { mutateAsync } = useMutation(postAnswer, {
  //   onError: console.error,
  // })

  function countAnswered(questionsObj) {
    if (!questionsObj) return 0
    return Object.keys(questionsObj).filter((k) => {
      const v = questionsObj[k]
      return v !== undefined && v !== null && String(v).trim() !== ''
    }).length
  }

  function saveProgress(values = {}, { isFinish = false, fillRemaining = false } = {}) {
    const normalizedValues = normalizeAnswers(values, subtype, questions)

    const storageKey = 'questionKraep'
    const existingRaw = localStorage.getItem(storageKey)
    const existing = existingRaw
      ? JSON.parse(existingRaw)
      : {
          userId: user.id,
          moduleID: Number(modules.id),
          data: [],
          globalCount: 0,
          time_start: new Date().toISOString(),
        }

    const idx = existing.data.findIndex((d) => d.subType === subtype.type_code)
    const prevData = idx > -1 ? existing.data[idx].data : {}

    let mergedQuestions = { ...(prevData.questions || {}), ...normalizedValues }

    // sanitize semua jawaban biar gak ada spasi doang
    mergedQuestions = Object.entries(mergedQuestions).reduce((acc, [key, val]) => {
      if (val == null) {
        // handle null & undefined
        acc[key] = ''
      } else if (typeof val === 'string') {
        acc[key] = val.trim() || ''
      } else {
        acc[key] = String(val) // fallback, kalau ada angka/boolean masukin jadi string
      }
      return acc
    }, {})

    if (fillRemaining) {
      for (let i = 1; i <= totalQuestions; i++) {
        const key = `question_${i}`
        if (!(key in mergedQuestions)) mergedQuestions[key] = ''
      }
    }

    const ignoreSubCount =
      subtype.type_code === 'ME_IMAGE' ||
      subtype.type_code.toLowerCase().toLowerCase().includes('_desc_ist')

    // di saveProgress
    const subCount = ignoreSubCount
      ? 0 // jangan nambah jawaban ignoreSubCount
      : fillRemaining
      ? totalQuestions
      : countAnswered(mergedQuestions)
    const status = isFinish || subCount >= totalQuestions ? 'finished' : 'inProgress'
    const currentPage = status === 'finished' ? 0 : Math.min(subCount + 1, totalQuestions)

    const subData = {
      questionID: Number(dataQuestion.id),
      minutes,
      seconds,
      currentPage,
      subCount,
      questions: mergedQuestions,
      status,
      totalQuestion: totalQuestions,
    }

    if (idx > -1) {
      existing.data[idx].data = subData
    } else {
      existing.data.push({
        subType: subtype.type_code,
        typeSubModul: subtype.type_sub_modul,
        data: subData,
      })
    }

    // existing.globalCount = existing.data.reduce((acc, d) => acc + (d.data?.subCount || 0), 0)
    existing.globalCount = existing.data
      .filter((d) => d.subType !== 'ME_IMAGE')
      .reduce((acc, d) => acc + (d.data?.subCount || 0), 0)

    localStorage.setItem(storageKey, JSON.stringify(existing))

    dispatch({ type: SET_GLOBAL_COUNT, payload: existing.globalCount })
    setGlobalIndex(existing.globalCount)

    if (status === 'finished') setSubtestFinished(true)
    return existing.globalCount
  }

  // function transformKeys(values) {
  //   const newValues = {}
  //   Object.entries(values).forEach(([key, val]) => {
  //     // hapus prefix "question_"
  //     const newKey = key.replace(/^question_/, '')
  //     newValues[newKey] = val
  //   })
  //   return newValues
  // }

  // const submitSnapshot = async (answersSnapshot) => {
  //   const normalized = normalizeAnswers(answersSnapshot, subtype, questions)
  //   const transformed = transformKeys(normalized)

  //   const payload = {
  //     id_user: user.id,
  //     id_modul,
  //     tipe_modul: subtype.type_sub_modul,
  //     type_soal: subtype.type_code,
  //     json_jawaban: JSON.stringify({ answer: transformed }),
  //   }
  //   return mutateAsync(payload)
  // }

  // --- FIX: globalIndex sync only, DO NOT TOUCH currentQuestionIndex ---
  useEffect(() => {
    try {
      const parsed = JSON.parse(localStorage.getItem('questionKraep') || '{}')
      if (parsed?.globalCount) setGlobalIndex(parsed.globalCount)
      dispatch({ type: SET_GLOBAL_COUNT, payload: parsed?.globalCount ?? 0 })
    } catch {}
    // eslint-disable-next-line
  }, [])

  useEffect(() => {
    dispatch({ type: SET_CURRENT_QUESTION, currentPage: currentQuestionIndex + 1 })
    // eslint-disable-next-line
  }, [])

  const handleNext = async () => {
    if (isProcessing) return
    setIsProcessing(true)
    try {
      const values = form.getValues()
      if (subtestFinished) {
        if (onFinish) onFinish()
        return
      }

      saveProgress(values, { isFinish: false })

      const nextIndex = currentQuestionIndex + 1
      const isLastQuestion = currentQuestionIndex >= maxCount || nextIndex > maxCount

      if ((isLastQuestion || isStop) && !hasFinalSubmittedRef.current) {
        hasFinalSubmittedRef.current = true
        try {
          // saveProgress(values, { isFinish: true, fillRemaining: isStop })
          // await submitSnapshot({ ...savedForThis?.data?.questions, ...values })
          saveProgress(values, { isFinish: true, fillRemaining: isStop })
          // ❌ jangan submit disini lagi
        } catch (e) {
          console.error('final submit failed', e)
        } finally {
          dispatch({ type: RESET_SUBTEST })
          setCurrentQuestionIndex(0)
          setSubtestFinished(true)
          try {
            const parsed = JSON.parse(localStorage.getItem('questionKraep') || '{}')
            if (parsed?.globalCount) {
              dispatch({ type: SET_GLOBAL_COUNT, payload: parsed.globalCount })
              setGlobalIndex(parsed.globalCount)
            }
          } catch {}
          if (onFinish) onFinish()
        }
        return
      }

      setCurrentQuestionIndex(nextIndex)
      dispatch({ type: NEXT_QUESTION })
      // setGlobalIndex((g) => g + 1) // optional local increment only
    } finally {
      setIsProcessing(false)
    }
  }

  useEffect(() => {
    if (!isStop || hasFinalSubmittedRef.current) return
    const values = form.getValues()
    ;(async () => {
      hasFinalSubmittedRef.current = true
      saveProgress(values, { isFinish: true, fillRemaining: true })
      try {
        // await submitSnapshot({ ...savedForThis?.data?.questions, ...values })
      } catch (e) {
        console.error('timeout final submit failed', e)
      } finally {
        dispatch({ type: RESET_SUBTEST })
        setCurrentQuestionIndex(0)
        setSubtestFinished(true)
        try {
          const parsed = JSON.parse(localStorage.getItem('questionKraep') || '{}')
          if (parsed?.globalCount) {
            dispatch({ type: SET_GLOBAL_COUNT, payload: parsed.globalCount })
            setGlobalIndex(parsed.globalCount)
          }
        } catch {}
        if (onFinish) onFinish()
      }
    })()
    // eslint-disable-next-line
  }, [isStop])

  // --- INIT STORAGE FOR SUBTYPE ---
  useEffect(() => {
    if (!subtype?.type_code) return
    try {
      const raw = localStorage.getItem('questionKraep')
      const parsed = raw ? JSON.parse(raw) : null
      const hasEntry = parsed?.data?.some((d) => d.subType === subtype.type_code)
      if (!hasEntry) saveProgress({}, { isFinish: false })
    } catch {
      saveProgress({}, { isFinish: false })
    }
    // eslint-disable-next-line
  }, [subtype?.type_code])

  // --- AUTOSAVE TIMER ONLY (minutes/seconds) ---
  useEffect(() => {
    if (!subtype?.type_code) return
    try {
      const raw = localStorage.getItem('questionKraep')
      const existing = raw ? JSON.parse(raw) : null
      const idx = existing?.data?.findIndex((d) => d.subType === subtype.type_code)
      if (existing && idx > -1) {
        existing.data[idx].data.minutes = minutes
        existing.data[idx].data.seconds = seconds
        localStorage.setItem('questionKraep', JSON.stringify(existing))

        // khusus ME_IMAGE: jangan sentuh globalCount
        if (subtype.type_code !== 'ME_IMAGE') {
          dispatch({ type: SET_GLOBAL_COUNT, payload: existing.globalCount + 1 })
          setGlobalIndex(existing.globalCount + 1)
        }
      } else saveProgress({}, { isFinish: false })
    } catch (e) {
      console.error('autosave timer failed', e)
    }
    // eslint-disable-next-line
  }, [minutes, seconds, subtype?.type_code])

  if (!questions || questions?.length === 0) return <Typography>Soal belum tersedia...</Typography>

  const q = questions[currentQuestionIndex]
  const currentChild = React.Children.toArray(children)[currentQuestionIndex]

  return (
    <Box>
      <QuestionHeader
        minutes={minutes}
        seconds={seconds}
        typeName={subtype?.type_name}
        showTimer={subtype?.freeze_time ? false : true}
      />
      <form
        onSubmit={(e) => {
          e.preventDefault()
          form.handleSubmit(() => handleNext())()
        }}
      >
        {currentChild ? (
          React.cloneElement(currentChild, { form, question: q, index: currentQuestionIndex })
        ) : (
          <>
            <Typography>
              {currentQuestionIndex + 1}. {q?.soal || '-'}
            </Typography>
            {q?.type_sub_modul === 'PILIHAN GANDA' &&
              [q?.pilihan_1, q?.pilihan_2, q?.pilihan_3, q?.pilihan_4, q?.pilihan_5]
                .filter(Boolean)
                .map((opt, i) => (
                  <label key={i} style={{ display: 'block' }}>
                    <input type="radio" value={opt} {...form.register(q.id)} />
                    {opt}
                  </label>
                ))}
          </>
        )}

        {/* <Box display="flex" justifyContent="flex-end">
          <Button type="submit" variant="contained" sx={{ mt: 2, mb: 4 }} disabled={isProcessing}>
            {currentQuestionIndex === totalQuestions - 1 ? stringButtonFinish : 'Next'}
          </Button>
        </Box> */}
      </form>
    </Box>
  )
})

WizardKraep.propTypes = {
  subtype: PropTypes.object.isRequired,
  questions: PropTypes.array.isRequired,
  id_modul: PropTypes.string.isRequired,
  onFinish: PropTypes.func,
}

export default WizardKraep
